import React from 'react';
import {useRegrouping} from './useRegrouping';
import {checkNodeForSmallMarginPadding, removeSmallPaddingMarginNode} from 'rendition-core';
import {useViewContext} from 'Contexts/ViewContext';
import {Cards} from '@phosphor-icons/react';
import {Separator} from './EditControls';

interface RoundedBorderItemProps {
    icon: JSX.Element;
    title: string;
    onClick?: () => void;
}

interface Props {
    onSelectNodes: (nodes: string[]) => void;
}

export const EditorSuggestions: React.FC<React.PropsWithChildren<Props>> = ({onSelectNodes}) => {
    const {groupNodes} = useRegrouping(onSelectNodes);
    const {updateSelectedNodes, selectedNodes} = useViewContext();
    const SuggestionList = [
        {
            preReq: () => {
                return selectedNodes.length > 1;
            },
            action: () => {
                groupNodes(selectedNodes);
            },
            icon: <Cards size={16} color="#ffffff" weight="bold" />,
            title: 'Group Selected Elements',
        },
        {
            preReq: () => selectedNodes.every((node) => checkNodeForSmallMarginPadding(node)),
            action: () => {
                updateSelectedNodes((node) => {
                    removeSmallPaddingMarginNode(node);
                });
            },
            icon: <RemoveMarginIcon size={16} />,
            title: 'Remove small padding & margin',
        },
    ];

    const activeOptions = SuggestionList.filter((option) => option.preReq());

    if (activeOptions.length === 0) {
        return <></>;
    }

    return (
        <div className="mt-2 flex w-full flex-col items-start justify-between gap-2">
            <h1 className="text-xs font-extrabold capitalize text-white">⚡️ Suggestions</h1>
            {activeOptions.map((option) => (
                <RoundedBorderItem
                    key={option.title}
                    icon={option.icon}
                    title={option.title}
                    onClick={() => {
                        option.action();
                    }}
                />
            ))}
            <div className="h-0.5" />
            <Separator />
        </div>
    );
};

const RoundedBorderItem: React.FC<RoundedBorderItemProps> = ({icon, title, onClick}) => {
    return (
        <button
            className="flex w-full items-center justify-between rounded-lg bg-white bg-opacity-20 p-2 hover:bg-gray-300 hover:bg-opacity-20"
            onClick={onClick}
        >
            <div className="flex items-center gap-2">
                {icon}
                <span className="overflow-hidden overflow-ellipsis whitespace-nowrap text-xs text-white">{title}</span>
            </div>
        </button>
    );
};

const RemoveMarginIcon = (props: {size: number}) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size} height={props.size} fill="#ffffff" viewBox="0 0 256 256">
        <path d="M50.34,117.66A8,8,0,0,1,56,104h64V32a8,8,0,0,1,16,0v72h64a8,8,0,0,1,5.66,13.66l-72,72a8,8,0,0,1-11.32,0ZM216,208H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16Z"></path>
    </svg>
);
